import {
  getLevelUpTargetDatesAndProgressStatusesApi,
  getLevelUpDetailsApi,
  getOrgFunctionsTreeApi,
  updateCompetencyTargetDateApi,
  updateEmployeeTargetRoleApi,
  updateCompetencyProgressStatusApi,
  getLevelUpCompetencyDetailsApi,
  createLevelUpCompetencyCommentApi,
  updateLevelUpCompetencyCommentApi,
  getLevelUpCompetencyCommentsApi,
  getCompetenciesForRoleApi,
  addCompetencyToAgendaApi,
} from 'api/growth/oneOnOne';
import PostFeedItemData from 'models/postFeedItemData';

import { POST_VISIBILITY_TYPES } from 'constants/ProjectConstants';

import { getApiBodyFromEditorStateAndMedia } from 'services/helpers/post';

import { ApiResponse } from './oneOnOneHelpers';

/* getAllRolesForFunction expect a functionObj 
      structure: {function, grades*, childFunctions} 
      gradesStructures: [{grade, roles}]

      it will all the roles associated with function.
  */
const getAllRolesForFunction = (functionObj) => {
  if (Array.isArray(functionObj?.grades)) {
    return functionObj.grades.reduce(
      (prev, gradeObj) => (Array.isArray(gradeObj?.roles) ? [...prev, ...gradeObj.roles.map((_) => _.role)] : prev),
      []
    );
  }
  return [];
};

/* 
  recursive funtion makeListOfFunctionsAndRoles expects list of functions.
  will recursively traverse child functions and return list of functions and roles.
*/
const makeListOfFunctionsAndRoles = (functions, functionNamePrefix = '') => {
  if (!Array.isArray(functions)) return [];

  return functions.reduce((prev, functionObj) => {
    const functionList = functionObj.childFunctions
      ? makeListOfFunctionsAndRoles(functionObj.childFunctions, `${functionNamePrefix}${functionObj.function.name} > `)
      : [];
    return [
      ...prev,
      {
        uuid: functionObj.function.uuid,
        name: `${functionNamePrefix}${functionObj.function.name}`,
        roles: getAllRolesForFunction(functionObj),
      },
      ...functionList,
    ];
  }, []);
};

export const getOrgFunctionsAndRolesList = () =>
  getOrgFunctionsTreeApi().then((res) => makeListOfFunctionsAndRoles(res.entity?.childFunctions));

export const getLevelUpDetails = ({ employeeId }) =>
  getLevelUpDetailsApi({ employeeId }).then((res) => ApiResponse.LevelUpDetails.fromJSON(res.entity));

export const getCompetenciesForRole = ({ functionId, roleId }) =>
  getCompetenciesForRoleApi({ functionId, roleId }).then((res) => res.entity);

export const getLevelUpTargetDatesAndProgressStatuses = ({ employeeId }) =>
  getLevelUpTargetDatesAndProgressStatusesApi({ employeeId }).then((res) => ({
    targetDateList: Array.isArray(res.entity.targetDateList)
      ? res.entity.targetDateList.map((date) => ApiResponse.LevelUpTargetDate.fromJSON(date))
      : [],
    progressList: Array.isArray(res.entity.progressList)
      ? res.entity.progressList.map((status) => ApiResponse.LevelUpProgressStatus.fromJSON(status))
      : [],
    riskEstimationList: Array.isArray(res.entity.riskEstimationList)
      ? res.entity.riskEstimationList.map((riskEstimation) =>
          ApiResponse.LevelUpRiskEstimationList.fromJSON(riskEstimation)
        )
      : [],
  }));

export const updateEmployeeTargetRole = ({ employeeId, jobFunctionId, jobRoleId }) =>
  updateEmployeeTargetRoleApi({
    employeeId,
    jobFunctionId,
    jobRoleId,
  }).then((res) => ApiResponse.LevelUpDetails.fromJSON(res.entity));

export const updateCompetencyTargetDate = ({
  employeeId,
  jobFunctionId,
  jobRoleId,
  competencyId,
  subCompetencyId,
  targetPeriodStart,
}) =>
  updateCompetencyTargetDateApi({
    employeeId,
    jobFunctionId,
    jobRoleId,
    competencyId,
    subCompetencyId,
    targetPeriodStart,
  }).then((res) => {});

export const updateCompetencyProgressStatus = ({
  employeeId,
  jobFunctionId,
  jobRoleId,
  competencyId,
  subCompetencyId,
  progress,
  comment,
}) =>
  updateCompetencyProgressStatusApi({
    employeeId,
    jobFunctionId,
    jobRoleId,
    competencyId,
    subCompetencyId,
    progress,
    comment,
  }).then((res) => {});

export const getLevelUpCompetencyDetails = ({
  employeeId,
  levelUpId = null,
  competencyId = null,
  levelUpCompetencyId = null,
}) =>
  getLevelUpCompetencyDetailsApi({
    employeeId,
    levelUpId,
    competencyId,
    levelUpCompetencyId,
  }).then((res) => ApiResponse.LevelUpCompetencyDetails.fromJSON(res.entity));

export const getLevelUpCompetencyComments = ({ levelUpCompetencyId, offset, limit }) =>
  getLevelUpCompetencyCommentsApi({
    levelUpCompetencyId,
    offset,
    limit,
  }).then((res) =>
    res.entity.map((commentJSON) => {
      const comment = new PostFeedItemData();
      comment.fromJSON(commentJSON);
      return comment;
    })
  );

export const createLevelUpCompetencyComment = ({
  commentEditorState,
  media,
  visibilityType,
  sentiment,
  isKudos = false,
  employeeId,
  competencyId,
  levelUpId,
  subCompetencyId,
}) => {
  const body = getApiBodyFromEditorStateAndMedia(commentEditorState, media, true, sentiment);
  body.visibilityType = visibilityType || POST_VISIBILITY_TYPES.global.name;

  return createLevelUpCompetencyCommentApi({
    commentBody: body,
    isKudos,
    employeeId,
    competencyId,
    levelUpId,
    subCompetencyId,
  }).then((res) => {
    const comment = new PostFeedItemData();
    comment.fromJSON(res.entity.comment);
    return { comment };
  });
};

export const updateLevelUpCompetencyComment = ({
  commentEditorState,
  media,
  visibilityType,
  sentiment,
  isKudos = false,
  commentId,
  employeeId,
  competencyId,
  levelUpId,
  subCompetencyId,
}) => {
  const body = getApiBodyFromEditorStateAndMedia(commentEditorState, media, true, sentiment);
  body.visibilityType = visibilityType || POST_VISIBILITY_TYPES.global.name;

  body.uuid = commentId;
  body.type = 'COMMENT';

  return updateLevelUpCompetencyCommentApi({
    commentBody: body,
    isKudos,
    employeeId,
    commentId,
    competencyId,
    levelUpId,
    subCompetencyId,
  }).then((res) => {
    const comment = new PostFeedItemData();
    comment.fromJSON(res.entity.comment);
    return { comment };
  });
};

export const addCompetencyToAgenda = ({
  employeeId,
  levelUpId = null,
  competencyId = null,
  labelType = null,
  uiKey,
}) => {
  const body = {
    levelUpInfo: {
      levelUpId,
      competencyId,
    },
    label: {
      labelType,
    },
    uiKey,
  };
  return addCompetencyToAgendaApi({
    employeeId,
    body,
  }).then((res) => res.entity);
};
